import React, { useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useOpenPath } from '../../../hooks/useOpenPath';
import { Alert, ButtonGroup, Container, ToggleButton } from 'react-bootstrap';
import { useDocumentTitle } from '../../../hooks/useDocumentTitle';
import { useListDetailScroll } from '../../../hooks/useListDetailScroll';
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen';
import { useUserActivity } from '../../../hooks/superadmin/useUserActivity';
import { formatDateOnlyForStorage } from '../../../Helpers/DateUtils';
import UserActivityChart from './UserActivityChart';
import UserActivityDetail from './UserActivityDetail';
import { BuildingActivityTable, UserActivityTable } from './UserActivityTables';
import {
    actionCount, buildingRows, isPersonRow, parseRange, RANGE_OPTIONS, rangeStart, sortUserRows, userRows, UserSortKey, weeklySeries,
} from './userActivityView';
import SuperadminBreadcrumb from '../SuperadminBreadcrumb';
import './UserActivityPage.css';

/**
 * Användaraktivitet (superadmin): who used the app, when and roughly what they
 * did, from the `activity/` log. Query params: `range`, `user`.
 */
const UserActivityPage: React.FC = () => {
    useDocumentTitle('Användaraktivitet');
    const location = useLocation();
    const openPath = useOpenPath();
    const [searchParams, setSearchParams] = useSearchParams();
    const range = parseRange(searchParams.get('range'));
    const selectedUser = searchParams.get('user');
    const [sortKey, setSortKey] = useState<UserSortKey>('lastActive');

    const isSmallScreen = useIsSmallScreen();
    const { containerRef, rememberListScroll } = useListDetailScroll<HTMLDivElement>(isSmallScreen && !!selectedUser);

    const end = formatDateOnlyForStorage(new Date());
    const start = rangeStart(range, end);
    const { entries, actors, buildingNames, loading, error } = useUserActivity(start, end);

    const series = useMemo(() => weeklySeries(entries, start, end), [entries, start, end]);
    const rows = useMemo(() => sortUserRows(userRows(entries, actors), sortKey), [entries, actors, sortKey]);
    const buildings = useMemo(() => buildingRows(entries), [entries]);

    const activeUsers = rows.filter((r) => isPersonRow(r) && r.activeDays > 0).length;
    const inactiveUsers = rows.filter((r) => isPersonRow(r) && r.activeDays === 0).length;
    const totalActions = entries.reduce((sum, e) => sum + actionCount(e), 0);
    const buildingName = (key: string) => buildingNames.get(key) ?? 'Okänd byggnad';

    const setParam = (key: string, value: string | null) => {
        const next = new URLSearchParams(searchParams);
        if (value === null) next.delete(key);
        else next.set(key, value);
        setSearchParams(next);
    };

    /** This page with one query param changed, so a row can open in a new tab. */
    const paramHref = (key: string, value: string) => {
        const next = new URLSearchParams(searchParams);
        next.set(key, value);
        return `${location.pathname}?${next}`;
    };

    const selectedRow = selectedUser ? rows.find((r) => r.id === selectedUser) : undefined;

    return (
        <>
        <SuperadminBreadcrumb title="Användaraktivitet" />
        <Container className="user-activity-page" ref={containerRef}>
            <h1 className="page-title">Användaraktivitet</h1>
            <p className="user-activity-muted">
                Vem som har använt appen, när och vad de gjorde. Loggen sparas i 24 månader.
            </p>

            <div className="user-activity-controls">
                <ButtonGroup className="user-activity-range" aria-label="Period">
                    {RANGE_OPTIONS.map((option) => (
                        <ToggleButton
                            key={option.value}
                            id={`ua-range-${option.value}`}
                            type="radio"
                            variant="outline-primary"
                            name="ua-range"
                            value={option.value}
                            checked={range === option.value}
                            onChange={() => setParam('range', option.value)}
                        >
                            {option.label}
                        </ToggleButton>
                    ))}
                </ButtonGroup>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading ? (
                <div className="user-activity-loading">
                    <div className="spinner-border text-primary" role="status" />
                    <p>Laddar aktivitet...</p>
                </div>
            ) : selectedUser && selectedRow ? (
                <UserActivityDetail
                    row={selectedRow}
                    entries={entries}
                    start={start}
                    end={end}
                    buildingName={buildingName}
                    onBack={() => setParam('user', null)}
                />
            ) : (
                <>
                    {selectedUser && <Alert variant="warning">Användaren finns inte i loggen eller användarlistan.</Alert>}
                    <div className="user-activity-stats">
                        <div className="user-activity-stat">
                            <span className="user-activity-stat-value">{activeUsers}</span>
                            <span className="user-activity-stat-label">Aktiva användare</span>
                        </div>
                        <div className="user-activity-stat">
                            <span className="user-activity-stat-value">{totalActions}</span>
                            <span className="user-activity-stat-label">Åtgärder</span>
                        </div>
                        <div className="user-activity-stat">
                            <span className="user-activity-stat-value">{inactiveUsers}</span>
                            <span className="user-activity-stat-label">Inte aktiva</span>
                        </div>
                    </div>

                    <div className="user-activity-card">
                        <h2 className="user-activity-section-title">Per vecka</h2>
                        <UserActivityChart data={series} />
                    </div>

                    <div className="user-activity-card" onClickCapture={rememberListScroll}>
                        <h2 className="user-activity-section-title">Användare</h2>
                        <UserActivityTable rows={rows} sortKey={sortKey} onSort={setSortKey} onOpen={(id, event) => openPath(paramHref('user', id), event)} />
                    </div>

                    <div className="user-activity-card">
                        <h2 className="user-activity-section-title">Byggnader</h2>
                        {buildings.length === 0
                            ? <p className="user-activity-muted">Ingen aktivitet i någon byggnad under perioden.</p>
                            : <BuildingActivityTable rows={buildings} buildingName={buildingName} />}
                    </div>
                </>
            )}
        </Container>
        </>
    );
};

export default UserActivityPage;
